/* eslint complexity: ["error", {max:14}] -- applies to entire file */
import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import isNil from "lodash/isNil";
import type { View } from "react-native";
import type { Edge } from "react-native-safe-area-context";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

import { useColorToken } from "@/hooks/useColorToken";
import { useAppSelector } from "@/store/hooks";
import { selectIsEventBannerVisible } from "@/store/reducers/userPreferences";
import Box from "@/ui/layout/Box";
import ScrollView from "@/ui/layout/ScrollView";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { isAndroid, isSamsungAPI35Plus, isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface ScreenContainerProps extends PropsWithChildren {
  backgroundColor?: string;
  isScrollView?: boolean;
  // isStackLayout is used to determine if the screen should have a bottom padding
  // currently in use on onboarding screens, this prop helps ensure that our UI is not cut off by the bottom navigation bar
  // on Samsung API 35+ devices
  isStackLayout?: boolean;
  shouldIgnoreSafeArea?: boolean;
}

const BaseTestID = "screen-container";

// StatusBarPadding in this repo is surprisingly complex and non-intuitive. An explanation follows.
//
// We can not apply a universal status bar padding on top of all components for two reasons:
// 1) in different places (eg, Home tab, Explore tab) we want the backgroundColor to be very different
// 2) in some places, to have the desired UI outcome, we have to insert the StatusBarPadding *into* a Header component
//   (eg,
//     ModalTitleHeader: if the StatusBarPadding was a separate UI component *before* ModalTitleHeader
//     you would see a block of white StatusBarPadding at the top, followed by the top shadow of ModalTitleHeader,
//     followed by ModalTitleHeader, and its bottom shadow. the desired UI outcome is no top shadow for ModalTitleHeader.
//     this is also true of several other similar use cases that expect a SafeAreaView at the top of the screen, and have StatusBarPadding inserted *into* those components:
//       LiveEventTabHeader, NetworkStatusBanner, etc.
//   )
//
//
// Furthermore, the rendering of StatusBarPadding is made more complex than needed due to a bug with SafeAreaView:
// SafeAreaView does not render useSafeAreaInsets().top on Android specifically
//    all other SafeArea padding is handled automatically on iOS/Android, EXCEPT this top padding, which is needed for the status bar
//    so we need to manually handle this ourselves
const StatusBarPadding = ({
  backgroundColor: colorKey = ColorToken.NeutralBackgroundPrimary,
}: {
  backgroundColor?: string;
}) => {
  // explicit empty color string "" indicates transparency
  const backgroundColorValue = useColorToken({ colorKey });
  const backgroundColor = colorKey === "" ? undefined : backgroundColorValue;

  const { top: platformPaddingTop } = useSafeAreaInsets();
  const [paddingTop, setPaddingTop] = useState<number | undefined>(undefined);

  const viewRef = useRef<View>(null);
  const onLayout = useCallback(() => {
    if (isNil(viewRef.current)) {
      return;
    }
    viewRef.current.measure(
      (
        _originX: number,
        _originY: number,
        _width: number,
        _height: number,
        componentX: number,
        componentY: number,
        // eslint-disable-next-line max-params
      ) => {
        // only display the StatusBarPadding if this component is at the very top of the screen
        // this is necessary because each Tab has its own StatusBarPadding, and so does the LiveEventBanner
        // however, only the StatusBarPadding at the very top of the screen should be rendered
        const isVeryTopOfScreen = componentX === 0 && componentY === 0;
        const newPaddingTop = isVeryTopOfScreen
          ? platformPaddingTop
          : undefined;
        setPaddingTop(newPaddingTop);
      },
    );
  }, [platformPaddingTop, viewRef]);

  return (
    <Box
      backgroundColor={backgroundColor}
      height={paddingTop}
      onLayout={onLayout}
      ref={viewRef}
      testID={joinTestIDs(BaseTestID, "status-bar-padding")}
    />
  );
};

const WebContentConstraints = isWeb()
  ? {
      alignSelf: "center" as const,
      maxWidth: LayoutToken.WebContentMaxWidth,
      width: "100%" as const,
    }
  : {};

const ScreenContainer = ({
  backgroundColor: colorKey = ColorToken.NeutralBackgroundPrimary,
  children,
  isScrollView = false,
  isStackLayout = false,
  shouldIgnoreSafeArea,
}: ScreenContainerProps) => {
  const backgroundColor = useColorToken({ colorKey });
  const webGutterBackground = useColorToken({
    colorKey: ColorToken.NeutralBackgroundPrimary,
  });
  const insets = useSafeAreaInsets();
  const paddingBottom = isSamsungAPI35Plus() ? insets.bottom : undefined;
  // the live-event banner renders above the tabs and already covers the top safe area;
  // when it's visible, skip our own top inset/status-bar padding to avoid a doubled gap
  const isEventBannerVisible = useAppSelector(selectIsEventBannerVisible);

  const webWrappedChildren = useMemo(() => {
    // eslint-disable-next-line react/jsx-no-useless-fragment -- sonarjs/function-return-type requires consistent JSX return
    if (!isWeb()) return <>{children}</>;
    return (
      <Box flex={1} style={{ backgroundColor }}>
        {children}
      </Box>
    );
  }, [backgroundColor, children]);

  if (shouldIgnoreSafeArea) {
    return (
      <Box
        flex={1}
        paddingBottom={paddingBottom}
        testID={joinTestIDs(BaseTestID, "ignore-safe-area")}
      >
        {isScrollView ? <ScrollView>{children}</ScrollView> : children}
      </Box>
    );
  }

  const safeAreaBackground = isWeb() ? webGutterBackground : backgroundColor;
  // exclude the top edge when the live-event banner already covers the top safe area
  const nonStackEdges: readonly Edge[] = isEventBannerVisible
    ? ["left", "right"]
    : ["top", "left", "right"];

  return (
    // DO NOT pass a ref to SafeAreaView: see JN-1545 for why this causes app crashes,
    <SafeAreaView
      edges={isStackLayout ? undefined : nonStackEdges}
      style={{
        backgroundColor: safeAreaBackground,
        flex: 1,
        paddingBottom: isStackLayout ? paddingBottom : undefined,
      }}
      testID={BaseTestID}
    >
      {/*
        SafeAreaView bug #2: SafeAreaView does not correctly apply the useSafeAreaInsets().top, only on Android
        so instead we manually add a View with the correct paddingTop/height here, called StatusBarPadding
        see: https://github.com/AppAndFlow/react-native-safe-area-context/issues/634
      */}
      {isAndroid() && !isEventBannerVisible && (
        <StatusBarPadding backgroundColor={colorKey} />
      )}
      {isScrollView ? (
        <ScrollView
          contentContainerStyle={{
            alignSelf: WebContentConstraints.alignSelf,
            backgroundColor,
            flexGrow: 1,
            maxWidth: WebContentConstraints.maxWidth,
            width: WebContentConstraints.width,
          }}
        >
          {children}
        </ScrollView>
      ) : (
        webWrappedChildren
      )}
    </SafeAreaView>
  );
};

const { WebContentMaxWidth } = LayoutToken;

export type { ScreenContainerProps };

export {
  BaseTestID,
  StatusBarPadding,
  WebContentConstraints,
  WebContentMaxWidth,
};
export default ScreenContainer;
